/**
 * Scrubbing for anything a client ships to an observability sink.
 *
 *  - {@link scrubRoute}: URL → templated route. NEVER returns a query string or
 *    hash, and replaces token/id-looking path segments. Several live routes carry
 *    a patient token in the path (`/intake/[token]`, `/consent/[token]`, …) and the
 *    sink table `observability.runtime_errors` is not a PHI store.
 *  - {@link scrubUrl}: same, but keeps the origin (for the legacy `url` field).
 *  - {@link scrubText}: credential redaction ({@link redactSecrets}) plus
 *    email / phone / SSN-shaped values → `[REDACTED_PII]`.
 *  - {@link scrubValue}: recursive scrub of a metadata tree (URL-ish keys go
 *    through {@link scrubUrl}; every string through {@link scrubText}).
 *
 * All helpers are pure and NEVER throw.
 */

import { redactSecrets } from "./redact";

/** Replacement marker for email / phone / SSN-shaped values. */
export const REDACTED_PII = "[REDACTED_PII]";

/** Placeholder for a path segment that looks like a token or an id. */
export const ID_SEGMENT = ":id";

// ---------------------------------------------------------------------------
// Route scrubbing
// ---------------------------------------------------------------------------

export interface ScrubRouteOptions {
  /**
   * Declared route templates, e.g. `["/intake/:token", "/treatment/:id"]`.
   * A `:name` segment matches any single non-empty segment; a trailing `*`
   * matches the rest of the path (zero or more segments). The FIRST matching template wins and is
   * returned verbatim, so the sink only ever sees the template.
   */
  templates?: readonly string[];
  /**
   * PHI mode: template EVERY all-digit segment (`/patients/42` → `/patients/:id`),
   * not only 4+ digit runs. Short numeric ids are still patient/record ids in
   * the medspa APIs; the default (off) keeps `/api/v1/…` and other short
   * version-ish segments readable for non-PHI apps.
   */
  strictIds?: boolean;
}

const ALL_DIGITS_RE = /^\d+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_TOKEN_RE = /^[A-Za-z0-9_-]{16,}$/;
const LONG_NUMBER_RE = /^\d{4,}$/;
const HEX_WITH_DIGIT_RE = /^(?=.*\d)[0-9a-f]{8,}$/i;
const JWT_PIECE_RE = /^eyJ[A-Za-z0-9_-]*$/;

function decodeSafe(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Does this single path segment look like a token or an id?
 *
 * True for: UUIDs, numbers of 4+ digits, runs of 16+ `[A-Za-z0-9_-]` chars,
 * hex strings of 8+ chars containing a digit, JWT pieces (`eyJ…`, or any
 * dot-separated piece that is itself a long token), and anything containing `@`
 * (an email in the path). Deliberately over-scrubs: prefer declaring a route
 * template over weakening this. With `strict` (PHI mode) ANY all-digit segment
 * is an id, whatever its length.
 */
export function looksLikeIdSegment(segment: string, strict = false): boolean {
  const s = decodeSafe(segment);
  if (s.length === 0) return false;
  if (s.includes("@")) return true;
  if (strict && ALL_DIGITS_RE.test(s)) return true;
  if (UUID_RE.test(s) || LONG_NUMBER_RE.test(s)) return true;
  for (const piece of s.split(".")) {
    if (LONG_TOKEN_RE.test(piece) || JWT_PIECE_RE.test(piece) || HEX_WITH_DIGIT_RE.test(piece)) {
      return true;
    }
  }
  return false;
}

/** Strip scheme + authority, query, and hash; return the bare path. */
function pathOf(raw: string): string {
  let s = raw.trim();
  const cut = s.search(/[?#]/);
  if (cut >= 0) s = s.slice(0, cut);
  const authority = s.match(/^(?:[a-z][a-z0-9+.-]*:)?\/\/[^/]*/i);
  if (authority) s = s.slice(authority[0].length) || "/";
  return s;
}

function matchesTemplate(pathSegments: string[], template: string): boolean {
  const tSegments = template.split("/");
  const wildcardTail = tSegments[tSegments.length - 1] === "*";
  const fixed = wildcardTail ? tSegments.slice(0, -1) : tSegments;
  if (wildcardTail ? pathSegments.length < fixed.length : pathSegments.length !== fixed.length) {
    return false;
  }
  return fixed.every((t, i) => {
    const p = pathSegments[i] ?? "";
    return t.startsWith(":") ? p.length > 0 : t === p;
  });
}

function normalizeForMatch(path: string): string[] {
  const trimmed = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return trimmed.split("/").map((seg) => seg.split(";")[0] ?? "");
}

/**
 * Reduce a URL or path to a templated route that is safe to ship.
 *
 * - Query string and hash are ALWAYS dropped.
 * - If the path matches a declared template, the template is returned.
 * - Otherwise every segment that {@link looksLikeIdSegment} becomes `:id`.
 */
export function scrubRoute(url: string | null | undefined, options: ScrubRouteOptions = {}): string {
  if (typeof url !== "string" || url.length === 0) return "";
  try {
    const path = pathOf(url);
    const segments = normalizeForMatch(path);
    for (const template of options.templates ?? []) {
      const normalizedTemplate = template.length > 1 ? template.replace(/\/+$/, "") : template;
      if (matchesTemplate(segments, normalizedTemplate)) return normalizedTemplate;
    }
    return path
      .split("/")
      .map((seg) => {
        const bare = seg.split(";")[0] ?? "";
        return looksLikeIdSegment(bare, options.strictIds === true) ? ID_SEGMENT : bare;
      })
      .join("/");
  } catch {
    return "";
  }
}

/**
 * Like {@link scrubRoute} but keeps `scheme://host` (the legacy `url` field of
 * a runtime error report carried the origin). Relative inputs stay relative.
 */
export function scrubUrl(url: string | null | undefined, options: ScrubRouteOptions = {}): string {
  if (typeof url !== "string" || url.length === 0) return "";
  const authority = url.trim().match(/^(?:[a-z][a-z0-9+.-]*:)?\/\/[^/?#]*/i);
  // Userinfo can carry credentials (`https://user:pw@host`); never echo it.
  const origin = authority ? authority[0].replace(/\/\/[^@/]*@/, "//") : "";
  return `${origin}${scrubRoute(url, options)}`;
}

// ---------------------------------------------------------------------------
// Text scrubbing
// ---------------------------------------------------------------------------

const SSN_RE = /(?<![\d-])\d{3}[- ]\d{2}[- ]\d{4}(?![\d-])/g;
// Every quantifier is bounded (RFC 5321 limits: 64-char local part, 63-char labels).
// An unbounded `[...]+@` is O(n^2) on a long '@'-less run, and message/stack text
// is attacker-influenced and up to tens of KB.
const EMAIL_RE = /[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){0,8}\.[A-Za-z]{2,24}/g;
// Formatted NANP phone numbers: (303) 555-0100, 303-555-0100, 303.555.0100,
// +1 303 555 0100. Bare 10-digit runs are NOT matched (unix timestamps / ids).
const PHONE_RE = /(?<![\w.])(?:\+?1[\s.-]?)?(?:\(\d{3}\)\s?|\d{3}[\s.-])\d{3}[\s.-]\d{4}(?![\w])/g;
const INTL_PHONE_RE = /(?<![\w.])\+\d{1,3}[\s.-]?\(?\d{1,4}\)?(?:[\s.-]?\d{2,4}){2,4}(?![\w])/g;

/**
 * Scrub credentials ({@link redactSecrets}) and PII-shaped values (email,
 * phone, SSN) from free text. Never throws; non-strings pass through.
 */
export function scrubText(text: string): string {
  if (typeof text !== "string" || text.length === 0) return text;
  try {
    return redactSecrets(text)
      .replace(SSN_RE, REDACTED_PII)
      .replace(EMAIL_RE, REDACTED_PII)
      .replace(PHONE_RE, REDACTED_PII)
      .replace(INTL_PHONE_RE, REDACTED_PII);
  } catch {
    return text;
  }
}

// Date-of-birth-shaped values. Numeric `M/D/YYYY` / `D-M-YYYY`, ISO `YYYY-MM-DD`
// (NOT when it is the date half of a `…T…` timestamp), and `March 3, 1985`.
const DOB_NUMERIC_RE = /(?<!\d)\d{1,2}[/-]\d{1,2}[/-](?:19|20)\d{2}(?!\d)/g;
const DOB_ISO_RE = /(?<!\d)(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])(?![\dT])/g;
const DOB_LONG_RE =
  /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]{0,6}\.?\s{1,3}\d{1,2}(?:st|nd|rd|th)?,?\s{1,3}(?:19|20)\d{2}\b/gi;

/**
 * Stricter {@link scrubText} for PHI apps: additionally replaces
 * date-of-birth-shaped values with `[REDACTED_PII]`. Used on console arguments
 * and error messages that may echo patient data. Never throws.
 */
export function scrubPhiText(text: string): string {
  if (typeof text !== "string" || text.length === 0) return text;
  try {
    return scrubText(text)
      .replace(DOB_NUMERIC_RE, REDACTED_PII)
      .replace(DOB_ISO_RE, REDACTED_PII)
      .replace(DOB_LONG_RE, REDACTED_PII);
  } catch {
    return text;
  }
}

// Absolute URL (`scheme://…`) OR a path token (`/…`) at the start of the text or
// after whitespace / `(` / `[` / `=` / `"` / `'`. A path after `:` (the `//` of
// an absolute URL) is never matched separately.
const EMBEDDED_URL_RE = /[a-z][a-z0-9+.-]*:\/\/[^\s"'<>`]+|(?<=^|[\s(["'=])\/[^\s"'<>`]*/gi;
const TRAILING_PUNCT_RE = /[)\]}.,;:!]+$/;

/**
 * Scrub every URL / path embedded in free text, then {@link scrubText} it.
 *
 * Absolute URLs go through {@link scrubUrl} (origin kept, query + hash dropped,
 * id segments templated); bare paths go through {@link scrubRoute}. Built for
 * Sentry span descriptions / transaction names such as
 * `GET https://api.example.com/api/v1/appointments?patient_id=123`
 * → `GET https://api.example.com/api/v1/appointments`. Never throws.
 */
export function scrubUrlsInText(text: string, options: ScrubRouteOptions = {}): string {
  if (typeof text !== "string" || text.length === 0) return text;
  try {
    const replaced = text.replace(EMBEDDED_URL_RE, (match) => {
      // Sentence punctuation glued to the URL ("(…/status?x=1)." ) is put back
      // after scrubbing — it carries no data, and the match deliberately
      // over-reaches so nothing in a query survives.
      const tail = match.match(TRAILING_PUNCT_RE)?.[0] ?? "";
      const core = tail ? match.slice(0, -tail.length) : match;
      return (core.startsWith("/") ? scrubRoute(core, options) : scrubUrl(core, options)) + tail;
    });
    return scrubText(replaced);
  } catch {
    return scrubText(text);
  }
}

// ---------------------------------------------------------------------------
// Metadata scrubbing
// ---------------------------------------------------------------------------

/** Keys whose string value is a URL / path / URI. */
const URL_KEY_RE = /url|uri|href|endpoint|referr?er|sourcefile|source_file|pathname/i;
const MAX_DEPTH = 8;

function looksLikeUrlValue(value: string): boolean {
  return value.startsWith("/") || /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
}

function scrubUrlLikeString(value: string, options: ScrubRouteOptions): string {
  if (/^(?:data|javascript):/i.test(value)) return `${value.slice(0, value.indexOf(":") + 1)}[REDACTED]`;
  if (!looksLikeUrlValue(value)) return scrubText(value);
  return scrubText(scrubUrl(value, options));
}

/**
 * Recursively scrub a metadata value. Strings under URL-ish keys are reduced
 * with {@link scrubUrl} (no query/hash, id segments templated); every other
 * string goes through {@link scrubText}. Returns a copy; never mutates.
 */
export function scrubValue(value: unknown, options: ScrubRouteOptions = {}, key = "", depth = 0): unknown {
  if (typeof value === "string") {
    return URL_KEY_RE.test(key) ? scrubUrlLikeString(value, options) : scrubText(value);
  }
  if (value === null || typeof value !== "object" || depth >= MAX_DEPTH) return value;
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, options, key, depth + 1));
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = scrubValue(v, options, k, depth + 1);
  }
  return out;
}
